<template>
  <div
    class="template-box"
    :class="{
      [actions.type.toLowerCase()]: true,
      'no-template': !actions.hasShipmentTemplate,
      droppable: dropZoneActive
    }"
    @dragenter.prevent="setDropzoneActive"
    @dragover.prevent="setDropzoneActive"
    @dragleave.prevent="setDropzoneInactive"
    @drop.prevent="onDrop"
  >
    <div class="template-inner">
      <div class="index">{{ index + 1 }}</div>
      <div class="content">
        <div class="icon-container">
          <img :src="require(`@/assets/images/${actions.icon}`)" :alt="actions.icon" class="icon" />
        </div>
        <h2>{{ actions.title }}</h2>
        <p class="action-description" v-html="actions.description" />

        <div class="bulk-update-template-dropdowns" v-if="actions.type === BULK_ACTIONS.DOWNLOAD && tab === HEADER_TAB_UPDATE">
          <div class="template-type">
            <DropdownSelectable
              ref="templateType"
              :dropItems="updateTemplates || []"
              label="Şablon Türü"
              isRadioGroup
              :dropdownStateProps="templateTypeDropdownState"
              :errorText="errors.templateTypeError"
              :isShowSearch="false"
              @selectedRadioItem="handleSelectedTemplateTypeItems($event)"
              @changedDropdownState="handleChangedTemplateTypeDropdownState($event)"
            />
          </div>
          <div class="template-group">
            <DropdownSelectable
              ref="templateProductGroup"
              btnSize="sm"
              label="Ürün Grubu"
              :errorText="isTemplateDisabled ? false : errors.templateProductGroupError"
              :isRadioGroup="false"
              :dropItems="templateProductGroupDropItems"
              :isShowSearch="false"
              :class="['bulk-dropdown', { 'is-disabled': isTemplateDisabled }]"
              @selectedItems="handleSelectedTemplateProductGroupItems($event)"
            />
          </div>
        </div>

        <div v-if="actions.type === BULK_ACTIONS.UPLOAD">
          <div v-if="fileRef" class="file-info">
            <button class="remove-file" @click="removeFile">×</button>
            <div class="file-details-container">
              <img src="@/assets/images/xls-file.svg" alt="Icon" class="icon" />
              <div class="file-details">
                <div class="file-name">{{ fileRef.name }}</div>
                <div class="file-size">{{ formattedFileSize }}</div>
              </div>
            </div>
            <div v-if="uploading" class="file-status">
              <img src="@/assets/images/icons/button-check.svg" />
              Dosya inceleniyor...
            </div>
            <div v-else>
              <div v-if="!fileError" class="file-status">
                <img src="@/assets/images/icons/button-check.svg" />
                Dosya yüklenmeye hazır.
              </div>
              <div v-if="fileError" class="file-status error">
                <img src="@/assets/images/icons/alert-circle.svg" />
                {{ fileError }}
              </div>
            </div>
          </div>

          <input type="file" ref="fileInput" @change="handleFileChange" hidden accept=".xlsx" />
        </div>

        <AppButton
          v-if="actions.ctaAction && !validationId"
          :title="actions.buttonText"
          primary
          md
          class="w-auto"
          :disabled="buttonShouldBeDisabled"
          @click="actions.ctaAction(getRefs)"
        />

        <AppButton
          v-else
          :title="'Dosyayı Sisteme Yükle'"
          class="w-auto action-button"
          primary
          :disabled="buttonShouldBeDisabled"
          md
          @click="uploadFile"
        />
        <p class="file-restrictions" v-if="actions.info && !fileRef" v-html="actions.info"></p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { DropdownSelectable, AppButton } from '@n11/so-ui-components';
import { BULK_ACTIONS } from "@/utils/enum/bulkActions";
import { HISTORY_TAB_UPLOAD, HEADER_TAB_UPLOAD, HEADER_TAB_UPDATE } from "@/utils/enum/bulkActionTabs";

const store = useStore();

const props = defineProps({
  index: Number,
  tab: String,
  action: Object,
  maxFileSize: {
    type: Number,
    default: 15, // MB cinsinden maksimum dosya boyutu
  },
  refreshRef: {
    refresh: Boolean,
    tab: String | null,
    taskId: Number | null,
  },
});

const emit = defineEmits(['refreshTable']);
const fileRef = ref(null);
const validationId = ref(null);
const dropZoneActive = ref(false);
const fileError = ref(null);
const uploading = ref(false);
const actions = ref(props.action);
const formattedFileSize = computed(() => fileRef.value ? (fileRef.value.size / 1024 / 1024).toFixed(2) + ' MB' : '');
const buttonShouldBeDisabled = computed(() => [BULK_ACTIONS.UPLOAD, BULK_ACTIONS.DOWNLOAD].includes(actions.value.type) && !actions.value.hasShipmentTemplate && props.tab === HEADER_TAB_UPLOAD);
const errors = computed(() => actions.value.errors);

const fileInput = ref(null);
const templateType = ref(null);
const isTemplateDisabled = ref(false);
const templateTypeDropdownState = ref(false);
const templateProductGroup = ref(null);
const uploadType = ref('EXCEL-CREATE');
const templateProductGroupDropItems = ref([
  { id: 'Suspended', name: 'Beklemede Olan Ürünler' },
  { id: 'Active', name: 'Onaylı Ürünler' }
]);

const updateTemplates = [
  { id: 'product', name: 'Ürün Bilgileri' },
  { id: 'stock_price', name: 'Stok & Fiyat Bilgileri' },
  { id: 'shipment_template', name: 'Teslimat Bilgileri'},
  { id: 'stock_code', name: 'Stok Kodu Bilgileri'},
  { id: 'product-safety-update', name: 'Ürün Güvenliği Bilgileri'},
  { id: 'product-nutritional-update', name: 'Ürün Besin Bilgileri ve Değerleri'},
  { id: 'product-safety-info-update', name: 'Ürün Güvenliği Ek Bilgileri'},
];

const getRefs = { fileInput, templateProductGroup, templateType };
const disabledProductGroupTemplates = ['product-safety-update', 'product-nutritional-update', 'product-safety-info-update'];

const handleSelectedTemplateTypeItems = (event) => {
  templateType.value = event;
  templateTypeDropdownState.value = false;
  templateProductGroup.value = null;

  if (event && event.id && disabledProductGroupTemplates.includes(event.id)) {
    isTemplateDisabled.value = true;
  } else {
    isTemplateDisabled.value = false;
  }

  if (event) delete actions.value.errors['templateTypeError'];
};

const handleChangedTemplateTypeDropdownState = (event) => {
  templateTypeDropdownState.value = event;
};

const handleSelectedTemplateProductGroupItems = (event) => {
  templateProductGroup.value = event;

  if (event) delete actions.value.errors['templateProductGroupError'];
};

const handleFileChange = (event) => validateFile(event.target.files[0]);
const handleFileDrop = (event) => {
  if (!event) return false;
  event.preventDefault();

  const droppedFile = event.dataTransfer.files[0];
  validateFile(droppedFile);
};

const validateFile = async (file) => {
  if (file && file.name.endsWith('.xlsx') && file.size <= props.maxFileSize * 1024 * 1024) {
    fileRef.value = file;
    uploading.value = true;
    uploadType.value = props.tab === HEADER_TAB_UPLOAD ? 'EXCEL-CREATE' : props.tab;
    try {
      const batchId = await store.dispatch("products/validateUpload", { file: file, type: uploadType.value });
      setTimeout(() => {
        uploading.value = false;
      }, 1000);

      if (batchId) {
        validationId.value = batchId;
      }
    } catch (err) {
      fileError.value = err.errorMessage;
      uploading.value = false;
    }
  } else {
    fileError.value = `Dosya .xlsx formatında ve ${props.maxFileSize} MB'den küçük olmalıdır.`;
    removeFile();
  }
};

const uploadFile = async (event) => {
  if (!event) return false;
  event.preventDefault();
  await upload();
};

const upload = async () => {
  if (validationId.value) {
    uploading.value = true;

    try {
      const confirmation = await store.dispatch("products/confirmUpload", validationId.value);
      uploading.value = false;

      if (confirmation && confirmation.id) {
        fileRef.value = null;
        validationId.value = null;

        removeFile();

        actions.value.buttonText = "Yeni Dosya Seç";
        actions.value.title = 'Dosyanı Aldık';
        actions.value.description = "Dosyanız sisteme işleniyor.<br /><u>Dosya Yükleme Geçmişim</u>’den detayları kontrol edebilir,<br />yeni ürünler eklemek için farklı bir dosya yükleyebilirsiniz.";
        actions.value.icon = "bulk-uploaded@2x.png";
        actions.value.info = null;

        emit('refreshTable', {
          tab: HISTORY_TAB_UPLOAD,
          taskId: confirmation.id,
          refresh: true,
          headerTab: props.tab
        });
      }
    } catch (err) {
      fileError.value = err.errorMessage;
      uploading.value = false;
    }
  }
};

onUnmounted(() => {
  if (actions.value && actions.value.errors) {
    actions.value.errors = {};
  }
});

const removeFile = () => {
  fileRef.value = null;
  fileError.value = null;
  validationId.value = null;
  const fileInput = document.querySelector('input[type="file"]');
  fileInput.value = null;
};

const setDropzoneActive = () => {
  if (actions.value.type === BULK_ACTIONS.UPLOAD) {
    dropZoneActive.value = true;
  }
};

const setDropzoneInactive = () => {
  if (actions.value.type === BULK_ACTIONS.UPLOAD) {
    dropZoneActive.value = false;
  }
};

const onDrop = (e) => {
  if (actions.value.type === BULK_ACTIONS.UPLOAD) {
    setDropzoneInactive();
    handleFileDrop(e);
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  .action-description :deep(u) {
    font-weight: 600;
    color: $colorM50;
    text-decoration: none;
  }
}

.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.template-box {
  border: 2px solid $colorM50;
  border-radius: 12px;
  width: 100%;
  text-align: center;
  position: relative;
  transition: all 150ms ease-in-out;

  & > .template-inner {
    border-radius: 12px;
    padding: 24px;
    min-height: 380px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $colorM05;
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1;
  }

  &.upload {
    border-color: transparent;
    &:before {
      content: '';
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23FF44EE' stroke-width='5' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
      background-position: center center;
      border-radius: 12px;
      left: -2px;
      top: -2px;
      right: -2px;
      bottom: -2px;
      position: absolute;
    }
  }

  &.download.no-template {
    border-color: $colorM50;
  }

  &.upload.no-template {
    &::before {
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23FF44EE' stroke-width='5' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
    }
  }

  &.droppable .template-inner {
    background-color: $colorM10;
  }
}

.index {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: $colorM50;
  color: white;
  font-weight: bold;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.icon-container {
  background-color: #e1d7ff;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
}

.icon {
  width: 90px;
  height: 90px;
}

.w-auto {
  width: auto;
}

h2 {
  margin: 8px 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  color: $colorN80;
}

p {
  color: $colorN80;
  font-size: 13px;
  line-height: 1.4;
  margin: 0px 0px 16px 0px;
  max-width: 460px;
}

.file-info {
  background-color: #fff;
  border: 1px solid #ddd;
  padding: 10px 24px 10px 10px;
  border-radius: 8px;
  margin-bottom: 20px;
  position: relative;
  max-width: 380px;
  width: 100%;

  .file-details-container {
    display: flex;
    gap: 16px;
    position: relative;

    img {
      width: 30px;
      height: 40px;
    }

    .file-details {
      width: 100%;
      text-align: left;
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
  }
}

.file-name {
  font-weight: bold;
  text-overflow: ellipsis;
  overflow: hidden;
  width: 300px;
  line-height: 20px;
}

.file-size {
  color: $colorD30;
  font-size: 14px;
  margin-top: 4px;
}

.file-status {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 13px;
  gap: 8px;
  color: $colorS50;

  &.error {
    color: $colorE50;
  }

  img {
    width: 16px;
    height: 16px;
  }
}

.file-restrictions {
  font-size: 12px;
  margin-top: 16px;
  position: absolute;
  bottom: 10px;
}

.file-restrictions::v-deep span {
  font-weight: bolder
}

.remove-file {
  position: absolute;
  right: 8px;
  top: 0px;
  font-size: 20px;
  padding: 0px;
}

.bulk-update-template-dropdowns {
  display: flex;
  gap: 12px;
  flex-direction: column;
  margin-bottom: 12px;
  max-width: 320px;
  text-align: left;
  width: 100%;

  .template-type {
    position: relative;
    z-index: 2;
    width: 100%;

    :deep(.errorText),
    :deep(.radioButton) {
      display: none;
    }

    :deep(.radioButton-container) {
      padding: 10px 14px;
    }
  }

  .template-group {
    :deep(.errorText, .actions-seperator) {
      display: none;
    }
  }
}
</style>
